/**
 * Public Schedule Page
 *
 * Public-facing display of studio class schedule.
 * No authentication required - designed for embedding or public viewing.
 * Applies studio theme (colors, fonts) for branded experience.
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useGetPublicScheduleQuery } from '../../api/schedulingApi';
import Watermark from '../../components/common/Watermark';
import { shouldShowWatermark } from '../../utils/watermark';
import ScheduleGrid from './components/ScheduleGrid';

const PublicSchedulePage: React.FC = () => {
  const { scheduleIdOrSlug } = useParams<{ scheduleIdOrSlug: string }>();

  const {
    data: scheduleData,
    isLoading,
    error,
  } = useGetPublicScheduleQuery(scheduleIdOrSlug || '', {
    skip: !scheduleIdOrSlug,
  });

  // Loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  // Error state
  if (error || !scheduleData) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">
          Unable to load class schedule. Please check the URL and try again.
        </Alert>
      </Container>
    );
  }

  const {
    config,
    studio,
    scheduled_classes: scheduledClasses,
    theme,
  } = scheduleData;

  // Apply studio theme colors using correct nested structure
  const primaryColor = theme?.tokens?.colors?.primary || '#1976d2';
  const fontFamily =
    theme?.tokens?.typography?.fontFamily?.body || 'Arial, sans-serif';
  const textColor = theme?.tokens?.colors?.text?.primary || '#000000';
  const backgroundColor = theme?.tokens?.colors?.background || '#ffffff';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor,
        fontFamily,
        py: 4,
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Back to External URL Button */}
        {config.external_url && (
          <Box mb={3}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              href={config.external_url}
              sx={{
                borderColor: primaryColor,
                color: primaryColor,
                '&:hover': {
                  borderColor: primaryColor,
                  backgroundColor: `${primaryColor}10`,
                },
              }}
            >
              Back
            </Button>
          </Box>
        )}

        {/* Page Title */}
        <Box mb={4}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontFamily,
              color: textColor,
              fontWeight: 'bold',
              textAlign: 'left',
            }}
          >
            {config.title || `${studio.name} Schedule`}
          </Typography>
        </Box>

        {/* Schedule Grid - Read-Only Mode */}
        {scheduledClasses.length === 0 ? (
          <Alert severity="info">
            No classes scheduled at this time. Please check back later.
          </Alert>
        ) : (
          <ScheduleGrid
            scheduledClasses={scheduledClasses}
            isEditable={false}
            theme={{
              primaryColor,
              fontFamily,
              textColor,
              backgroundColor,
            }}
          />
        )}

        {/* Footer with Back Button (if external URL exists) */}
        {config.external_url && (
          <Box mt={6} textAlign="center">
            <Button
              variant="contained"
              href={config.external_url}
              sx={{
                backgroundColor: primaryColor,
                fontFamily,
                '&:hover': {
                  backgroundColor: `${primaryColor}dd`,
                },
              }}
            >
              Back
            </Button>
          </Box>
        )}

        {/* Watermark (for free tier only) */}
        <Watermark
          textColor={textColor}
          shouldDisplay={shouldShowWatermark(studio.owner_plan_slug)}
        />
      </Container>
    </Box>
  );
};

export default PublicSchedulePage;
